import { useId, useState, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';

const BASE =
  'w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca disabled:opacity-60';

export function Campo({
  rotulo,
  children,
  dica,
  erro,
}: {
  rotulo: string;
  children: ReactNode;
  dica?: string;
  erro?: string | null;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium">{rotulo}</span>
      {children}
      {erro ? (
        <span className="text-xs text-pend">{erro}</span>
      ) : dica ? (
        <span className="text-xs text-tinta-3">{dica}</span>
      ) : null}
    </label>
  );
}

export function Texto({
  rotulo,
  valor,
  aoMudar,
  dica,
  obrigatorio,
  desabilitado,
  placeholder,
}: {
  rotulo: string;
  valor: string | null;
  aoMudar: (v: string | null) => void;
  dica?: string;
  obrigatorio?: boolean;
  desabilitado?: boolean;
  placeholder?: string;
}) {
  return (
    <Campo rotulo={rotulo} dica={dica}>
      <input
        type="text"
        required={obrigatorio}
        disabled={desabilitado}
        placeholder={placeholder}
        value={valor ?? ''}
        onChange={(e) => aoMudar(e.target.value === '' ? null : e.target.value)}
        className={BASE}
      />
    </Campo>
  );
}

/**
 * Entrada de dinheiro que grava CENTAVOS INTEIROS.
 *
 * A pessoa digita "38,90" e o campo devolve 3890. O valor fracionário nunca
 * existe nem por um instante: a conversão acontece na digitação, e a regra do
 * Firestore recusa qualquer coisa que não seja inteiro.
 *
 * Campo vazio devolve null, não zero — ausente nunca vira zero (RN-044).
 */
export function CampoReais({
  rotulo,
  cents,
  aoMudar,
  dica,
  desabilitado,
}: {
  rotulo: string;
  cents: number | null;
  aoMudar: (v: number | null) => void;
  dica?: string;
  desabilitado?: boolean;
}) {
  const [texto, setTexto] = useState(cents === null ? '' : (cents / 100).toFixed(2).replace('.', ','));
  const id = useId();

  function digitar(bruto: string) {
    setTexto(bruto);
    const limpo = bruto.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
    if (limpo.trim() === '') {
      aoMudar(null);
      return;
    }
    const n = Number(limpo);
    aoMudar(Number.isFinite(n) ? Math.round(n * 100) : null);
  }

  return (
    <Campo rotulo={rotulo} dica={dica}>
      <div className="relative">
        <span className="absolute top-1/2 left-2.5 -translate-y-1/2 font-mono text-xs text-tinta-3">R$</span>
        <input
          id={id}
          type="text"
          inputMode="decimal"
          disabled={desabilitado}
          value={texto}
          onChange={(e) => digitar(e.target.value)}
          onBlur={() => setTexto(cents === null ? '' : (cents / 100).toFixed(2).replace('.', ','))}
          placeholder="0,00"
          className={`${BASE} pl-9 text-right font-mono tabular-nums`}
        />
      </div>
    </Campo>
  );
}

/**
 * Percentual gravado em PONTOS-BASE. A pessoa digita "14,5" e o campo devolve
 * 1450.
 *
 * Campo vazio devolve `null`, não zero — e é a diferença que mais importa nesta
 * tela: comissão zero é uma afirmação sobre o negócio ("não pago comissão"),
 * enquanto comissão em branco é a ausência de uma informação que ninguém
 * levantou ainda. Tratar as duas igual produz um lucro que parece conferido e
 * está inflado (RN-051).
 */
export function CampoPercentual({
  rotulo,
  bps,
  aoMudar,
  dica,
  desabilitado,
}: {
  rotulo: string;
  bps: number | null;
  aoMudar: (v: number | null) => void;
  dica?: string;
  desabilitado?: boolean;
}) {
  const [texto, setTexto] = useState(bps === null ? '' : String(bps / 100).replace('.', ','));

  function digitar(bruto: string) {
    setTexto(bruto);
    const limpo = bruto.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
    if (limpo.trim() === '') {
      aoMudar(null);
      return;
    }
    const n = Number(limpo);
    aoMudar(Number.isFinite(n) ? Math.round(n * 100) : null);
  }

  return (
    <Campo rotulo={rotulo} dica={dica}>
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          disabled={desabilitado}
          value={texto}
          onChange={(e) => digitar(e.target.value)}
          onBlur={() => setTexto(bps === null ? '' : String(bps / 100).replace('.', ','))}
          placeholder="em branco = não informado"
          className={`${BASE} pr-7 text-right font-mono tabular-nums`}
        />
        <span className="absolute top-1/2 right-2.5 -translate-y-1/2 font-mono text-xs text-tinta-3">
          %
        </span>
      </div>
    </Campo>
  );
}

/** Quantidade em milésimos. "1,5" vira 1500. */
export function CampoQuantidade({
  rotulo,
  milli,
  aoMudar,
  dica,
  desabilitado,
}: {
  rotulo: string;
  milli: number | null;
  aoMudar: (v: number | null) => void;
  dica?: string;
  desabilitado?: boolean;
}) {
  const [texto, setTexto] = useState(milli === null ? '' : String(milli / 1000).replace('.', ','));

  function digitar(bruto: string) {
    setTexto(bruto);
    const limpo = bruto.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
    if (limpo.trim() === '') {
      aoMudar(null);
      return;
    }
    const n = Number(limpo);
    aoMudar(Number.isFinite(n) ? Math.round(n * 1000) : null);
  }

  return (
    <Campo rotulo={rotulo} dica={dica}>
      <input
        type="text"
        inputMode="decimal"
        disabled={desabilitado}
        value={texto}
        onChange={(e) => digitar(e.target.value)}
        className={`${BASE} text-right font-mono tabular-nums`}
      />
    </Campo>
  );
}

export function CampoInteiro({
  rotulo,
  valor,
  aoMudar,
  dica,
  sufixo,
  desabilitado,
}: {
  rotulo: string;
  valor: number | null;
  aoMudar: (v: number | null) => void;
  dica?: string;
  sufixo?: string;
  desabilitado?: boolean;
}) {
  return (
    <Campo rotulo={rotulo} dica={dica}>
      <div className="relative">
        <input
          type="number"
          step="1"
          disabled={desabilitado}
          value={valor ?? ''}
          onChange={(e) => aoMudar(e.target.value === '' ? null : Math.round(Number(e.target.value)))}
          className={`${BASE} text-right font-mono tabular-nums ${sufixo ? 'pr-10' : ''}`}
        />
        {sufixo && (
          <span className="absolute top-1/2 right-2.5 -translate-y-1/2 font-mono text-xs text-tinta-3">
            {sufixo}
          </span>
        )}
      </div>
    </Campo>
  );
}

/** Lista de termos livres — palavras-chave e sinônimos, que alimentam a busca de mercado. */
export function CampoTermos({
  rotulo,
  termos,
  aoMudar,
  dica,
  desabilitado,
}: {
  rotulo: string;
  termos: string[];
  aoMudar: (v: string[]) => void;
  dica?: string;
  desabilitado?: boolean;
}) {
  const [novo, setNovo] = useState('');

  function adicionar() {
    const t = novo.trim();
    if (t && !termos.includes(t)) aoMudar([...termos, t]);
    setNovo('');
  }

  return (
    <Campo rotulo={rotulo} dica={dica}>
      <div className="flex flex-wrap gap-1.5">
        {termos.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded bg-marca/10 px-2 py-0.5 text-xs text-marca"
          >
            {t}
            {!desabilitado && (
              <button
                type="button"
                onClick={() => aoMudar(termos.filter((x) => x !== t))}
                aria-label={`Remover ${t}`}
                className="hover:text-pend"
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {!desabilitado && (
        <input
          type="text"
          value={novo}
          onChange={(e) => setNovo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              adicionar();
            }
          }}
          onBlur={adicionar}
          placeholder="digite e tecle Enter"
          className={`${BASE} mt-1`}
        />
      )}
    </Campo>
  );
}

/**
 * Campo de senha com o olhinho para conferir o que foi digitado.
 *
 * Ele existe porque senha errada em campo mascarado não tem sintoma: a pessoa
 * repete o mesmo erro de digitação e conclui que a senha é que está errada.
 *
 * Quatro cuidados que fazem diferença aqui:
 *
 * - `type="button"`. Dentro de um formulário, botão sem tipo é `submit` por
 *   padrão — clicar no olhinho enviaria o formulário com a senha pela metade.
 * - Sempre começa OCULTA. Quem quiser ver, pede.
 * - O rótulo do botão diz a AÇÃO e muda com o estado, para quem usa leitor de
 *   tela saber o que vai acontecer, não o que está acontecendo.
 * - Trocar o `type` do input preserva o valor e o preenchimento automático do
 *   gerenciador de senhas — o que não aconteceria com dois inputs alternados.
 */
export function CampoSenha({
  rotulo,
  valor,
  aoMudar,
  dica,
  autoComplete = 'current-password',
  minimo,
  obrigatorio = true,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  dica?: string;
  autoComplete?: 'current-password' | 'new-password';
  minimo?: number;
  obrigatorio?: boolean;
}) {
  const [visivel, setVisivel] = useState(false);

  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{rotulo}</span>
      <div className="relative">
        <input
          type={visivel ? 'text' : 'password'}
          required={obrigatorio}
          minLength={minimo}
          autoComplete={autoComplete}
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          className="w-full rounded border border-regua-2 bg-papel py-2 pr-10 pl-3 text-sm outline-none focus:border-marca"
        />
        <button
          type="button"
          onClick={() => setVisivel((v) => !v)}
          aria-label={visivel ? 'Ocultar senha' : 'Mostrar senha'}
          title={visivel ? 'Ocultar senha' : 'Mostrar senha'}
          className="absolute top-1/2 right-1 -translate-y-1/2 rounded p-1.5 text-tinta-3 hover:text-marca focus:text-marca focus:outline-none"
        >
          {visivel ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
        </button>
      </div>
      {dica && <span className="text-xs text-tinta-3">{dica}</span>}
    </label>
  );
}
